// src/components/AdminUserManagement.js
import React, { useState, useEffect, useCallback } from 'react';

// --- 3rd Party Libraries ---
import {
    Box, Typography, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Button, TextField, Paper, Alert,
    CircularProgress, Dialog, DialogContent, DialogTitle, DialogActions,
    IconButton, Tooltip, Divider, Select, MenuItem, FormControl, FormHelperText,
    InputLabel, Grid, Chip, Fade
} from '@mui/material';
import {
    Edit as EditIcon,
    Delete as DeleteIcon,
    VpnKey as ResetPasswordIcon,
    ArrowBack as ArrowBackIcon,
    Add as AddIcon,
    Search as SearchIcon,
    Refresh as RefreshIcon,
    Person as PersonIcon
} from '@mui/icons-material';
import { collection, getDocs, doc, query, where, writeBatch, or, getDoc, orderBy } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { getAuth, sendPasswordResetEmail } from 'firebase/auth';
import { toast } from 'react-toastify';
import TwoFactorAuthCard from './TwoFactorAuthCard'; // Adjust path based on folder structure. If AdminUserManagement is in src/components, this is correct.

// --- Project Files ---
import { db, functions } from '../firebase';
import ClientManagementDashboard from './ClientManagementDashboard';
import CreateUserForm from './Admin/CreateUserForm';
import AssignTemplateModal from './Admin/AssignTemplateModal';
// Cloud function references
const adminUpdateUserCallable = httpsCallable(functions, 'adminUpdateUser');

export default function AdminUserManagement({ user: adminUser }) {
    // --- STATE MANAGEMENT ---
    const [users, setUsers] = useState([]);
    const [filteredUsers, setFilteredUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState('');
    const [search, setSearch] = useState('');
    const [isCreateUserModalOpen, setIsCreateUserModalOpen] = useState(false);

    // Drill-down view state
    const [selectedUser, setSelectedUser] = useState(null);
    const [clientsOfSelectedUser, setClientsOfSelectedUser] = useState([]);
    const [loadingClients, setLoadingClients] = useState(false);
    const [selectedClient, setSelectedClient] = useState(null);

    // Dialog states
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [userToDelete, setUserToDelete] = useState(null);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [editFormData, setEditFormData] = useState({ name: '', userType: '', claudeOpusFrequency: '', organizationId: '' });
    const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
    const [assignedTemplateName, setAssignedTemplateName] = useState('Loading...');
    const [organizations, setOrganizations] = useState([]); // NEW
    const [loadingOrganizations, setLoadingOrganizations] = useState(true); // NEW
    
    const auth = getAuth();

    // --- UTILITY FUNCTIONS ---
    const getUserType = (user) => {
        if (user.hasMultipleClients) return 'Advisor';
        return 'CEO/Direct';
    };
    
    const getActiveClientCount = (user) => {
        return user.activeClientCount || 0;
    };

    const formatGoLiveDate = (user) => {
        if (!user.goLiveDate) return 'Not set';
        try {
            if (user.goLiveDate.toDate) {
                return user.goLiveDate.toDate().toLocaleDateString();
            }
            return new Date(user.goLiveDate).toLocaleDateString();
        } catch (err) {
            return 'Invalid date';
        }
    };

    // --- DATA FETCHING ---
    const fetchAllUsers = useCallback(async (showRefreshingIndicator = false) => {
    if (showRefreshingIndicator) {
        setRefreshing(true);
    } else {
        setLoading(true);
    }
    setError('');

// USER VISIBILITY RULES: 
// 
// userPortalAccess: 
// - true or missing => user can access User Portal; show in AdminUserManagement 
// - false => firm-only user; hide from AdminUserManagement 
// 
// isOrgAdmin: // - true => has Firm Portal access (legacy name; means "hasFirmPortalAccess")
// - false or missing => no Firm Portal access 
// 
// AdminUserManagement: shows all users where userPortalAccess !== false

    try {
        console.log('[DEBUG] Fetching all users...');
const querySnapshot = await getDocs(collection(db, 'users'));
const allUsers = querySnapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
}));

// Filter to users with User Portal access
// userPortalAccess: true or missing = has access (default behavior)
// userPortalAccess: false = firm-only user, exclude from User Management
const usersWithPortalAccess = allUsers.filter(user => user.userPortalAccess !== false);

console.log("ALL USERS FETCHED FROM FIRESTORE:", allUsers.length);
console.log("USERS WITH PORTAL ACCESS:", usersWithPortalAccess.length);
console.log(`[DEBUG] Fetched ${usersWithPortalAccess.length} users with portal access`);
setUsers(usersWithPortalAccess);
        
        if (showRefreshingIndicator) {
    toast.success(`Refreshed ${usersWithPortalAccess.length} users`);
}
    } catch (err) {
        console.error('[ERROR] Fetching users:', err);
        const errorMessage = `Failed to fetch users: ${err.message}`;
        setError(errorMessage);
        toast.error(errorMessage);
    } finally {
        setLoading(false);
        setRefreshing(false);
    }
}, []); // ← Empty dependency array fixes the infinite loop

    const fetchClientsForUser = useCallback(async (user) => {
        if (!user) return;
        setLoadingClients(true);
        
        const timeoutId = setTimeout(() => {
            setLoadingClients(false);
            toast.error('Client fetch timed out');
        }, 10000);

        try {
            console.log(`[DEBUG] Fetching clients for user: ${user.id}`);
            const clientsQuery = query(
                collection(db, 'clients'), 
                or(
                    where('fcfoId', '==', user.id),
                    where('userId', '==', user.id)
                )
            );
            const querySnapshot = await getDocs(clientsQuery);
            const clientList = querySnapshot.docs.map(doc => ({ 
                id: doc.id, 
                ...doc.data() 
            }));
            
            console.log(`[DEBUG] Found ${clientList.length} clients for user ${user.id}`);
            setClientsOfSelectedUser(clientList);
            clearTimeout(timeoutId);
        } catch (err) {
            console.error("Error fetching clients for user:", err);
            setError("Failed to fetch associated clients.");
            toast.error('Failed to fetch clients');
            clearTimeout(timeoutId);
        } finally {
            setLoadingClients(false);
        }
    }, []);

    // --- EFFECTS ---
    useEffect(() => {
        const fetchTemplateName = async () => {
            if (!selectedUser) {
                setAssignedTemplateName(''); // Clear when no user is selected
                return;
            }

            if (selectedUser.assignedQuestionnaireTemplateId) {
                setAssignedTemplateName('Loading...');
                try {
                    const templateRef = doc(db, 'questionnaireTemplates', selectedUser.assignedQuestionnaireTemplateId);
                    const templateSnap = await getDoc(templateRef);
                    if (templateSnap.exists()) {
                        const template = templateSnap.data();
                        setAssignedTemplateName(`${template.name} (v${template.version})`);
                    } else {
                        setAssignedTemplateName('Assigned template not found');
                    }
                } catch (error) {
                    console.error("Error fetching template name:", error);
                    setAssignedTemplateName('Error loading template');
                }
            } else {
                setAssignedTemplateName('Not Assigned');
            }
        };

        fetchTemplateName();
    }, [selectedUser]);
    
    useEffect(() => {
        fetchAllUsers();
    }, [fetchAllUsers]);

    useEffect(() => {
        if (selectedUser) {
            fetchClientsForUser(selectedUser);
        }
    }, [selectedUser, fetchClientsForUser]);

    // ADD THIS CORRECTED BLOCK
    useEffect(() => {
        let filtered = users;
        if (search) {
        filtered = users.filter(u =>
            (u.name?.toLowerCase().includes(search.toLowerCase())) ||
            (u.email?.toLowerCase().includes(search.toLowerCase()))
        );
    }
    setFilteredUsers(filtered);
}, [search, users]);

    // Loading timeout effect
    useEffect(() => {
        if (loading) {
            const timer = setTimeout(() => {
                setError('Loading is taking longer than expected. Please check your connection.');
                setLoading(false);
            }, 10000);
            return () => clearTimeout(timer);
        }
    }, [loading]);

    // NEW: Fetch organizations for the dropdown
  useEffect(() => {
    const fetchOrganizations = async () => {
      try {
        const orgsQuery = query(collection(db, 'organizations'), orderBy('name', 'asc'));
        const querySnapshot = await getDocs(orgsQuery);
        const orgsData = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setOrganizations(orgsData);
      } catch (err) {
        console.error("Error fetching organizations:", err);
        toast.error("Could not load organizations.");
      } finally {
        setLoadingOrganizations(false);
      }
    };
    fetchOrganizations();
  }, []);

  console.log("RENDERING TABLE WITH filteredUsers:", filteredUsers);
  // --- EVENT HANDLERS ---
    const handleAssignTemplateSave = async (template) => {
        if (!selectedUser) {
            toast.error("No user selected.");
            return;
        }

    const toastId = toast.loading("Assigning template...");
        try {
    // Use the secure backend function instead of a direct write
    await adminUpdateUserCallable({
        userId: selectedUser.id,
        assignedQuestionnaireTemplateId: template.id,
        assignedQuestionnaireTemplateVersion: template.version
    });

    toast.update(toastId, { render: "Template assigned successfully!", type: "success", isLoading: false, autoClose: 4000 });
    setIsAssignModalOpen(false);
    // Refresh the specific user's data locally for an instant UI update
    setSelectedUser(prev => ({
        ...prev,
        assignedQuestionnaireTemplateId: template.id,
        assignedQuestionnaireTemplateVersion: template.version
    }));
} catch (error) {
            console.error("Error assigning template to user:", error);
            toast.update(toastId, { render: `Failed to assign template: ${error.message}`, type: "error", isLoading: false, autoClose: 5000 });
            // re-throw to be caught by modal if needed
            throw error;
        }
    };

    const handleSelectUser = (user) => {
        console.log(`[DEBUG] Selected user: ${user.name} (${user.id})`);
        setSelectedUser(user);
        setIsEditing(false);
        setError('');
    };

    const handleBackToUserList = () => {
        setSelectedUser(null);
        setSelectedClient(null);
        setIsCreateUserModalOpen(false);
        setIsEditing(false);
        setError('');
    };

    const handleSelectClient = (client) => {
        console.log(`[DEBUG] Selected client: ${client.name} (${client.id})`);
        setSelectedClient(client);
    };

    const handleBackToUserDetails = () => {
        setSelectedClient(null);
    };

    const handleUserCreated = (newUser) => {
        setIsCreateUserModalOpen(false);
        fetchAllUsers(true); // Refresh with indicator
        toast.success(`User ${newUser?.name || 'Unknown'} created successfully!`);
    };

    const handleRefreshUsers = () => {
        fetchAllUsers(true);
    };

    // Edit user handlers
    const handleEditClick = () => {
        if (!selectedUser) return;
         setEditFormData({
    name: selectedUser.name || '',
    userType: getUserType(selectedUser),
    claudeOpusFrequency: selectedUser.claudeOpusFrequency || 0, // Default to 0 if not set
    organizationId: selectedUser.organizationId || '' // NEW
  });
        setIsEditing(true);
        setError('');
    };

    const handleCancelEdit = () => {
        setIsEditing(false);
        setError('');
        // Reset form data
        setEditFormData({ name: '', userType: '' });
    };

    const handleFormChange = (e) => {
        const { name, value } = e.target;
        setEditFormData(prev => ({ ...prev, [name]: value }));
        // Clear error when user starts typing
        if (error && name === 'name' && value.trim()) {
            setError('');
        }
    };

    const handleSaveChanges = async () => {
        if (!selectedUser) return;
        
        // Validation
        if (!editFormData.name.trim()) {
            setError('Name is required');
            return;
        }

        if (editFormData.name.trim().length < 2) {
            setError('Name must be at least 2 characters long');
            return;
        }

        setIsSaving(true);
        setError('');
        
        try {
            console.log(`[DEBUG] Updating user ${selectedUser.id} with data:`, editFormData);
            
            await adminUpdateUserCallable({
  userId: selectedUser.id,
  fullName: editFormData.name.trim(),
  userType: editFormData.userType,
  claudeOpusFrequency: Number(editFormData.claudeOpusFrequency) || 0,
  organizationId: editFormData.organizationId || null // NEW
});
            
            toast.success("User profile updated successfully!");
            
            // Update local state for instant UI refresh
            const updatedUser = {
  ...selectedUser,
  name: editFormData.name.trim(),
  userType: editFormData.userType,
  hasMultipleClients: editFormData.userType === 'Advisor',
  organizationId: editFormData.organizationId || null // NEW
};
            setSelectedUser(updatedUser);
            
            // Update users list
            setUsers(prev => prev.map(u => 
                u.id === selectedUser.id ? updatedUser : u
            ));
            
            setIsEditing(false);
        } catch (err) {
            console.error("Error updating user:", err);
            const errorMessage = err.message || "Failed to update user.";
            setError(errorMessage);
            toast.error(`Update failed: ${errorMessage}`);
        } finally {
            setIsSaving(false);
        }
    };

    // Replace your handleResetPassword function with this updated version:

const handleResetPassword = async (email) => {
    if (!email) {
        toast.error("User email is not available.");
        return;
    }

    try {
        console.log(`[DEBUG] Sending password reset email to: ${email}`);

        // Dynamically get the auth domain from environment variables
const authDomain = process.env.REACT_APP_FIREBASE_AUTH_DOMAIN;

const actionCodeSettings = {
    url: `https://${authDomain}/reset-password`,
    handleCodeInApp: true,
};

        await sendPasswordResetEmail(auth, email, actionCodeSettings);
        toast.success(`Password reset email sent to ${email}`);
    } catch (error) {
        console.error("Error sending password reset email:", error);
        let errorMessage = 'Failed to send reset email';
        if (error.code === 'auth/user-not-found') {
            errorMessage = 'No user found with this email address';
        } else if (error.code === 'auth/invalid-email') {
            errorMessage = 'Invalid email address';
        } else if (error.message) {
            errorMessage = error.message;
        }
        toast.error(errorMessage);
    }
};

    const openDeleteDialog = (user) => {
        setUserToDelete(user);
        setDeleteDialogOpen(true);
    };

    const closeDeleteDialog = () => {
        setDeleteDialogOpen(false);
        setUserToDelete(null);
    };
    
    // --- DELETION LOGIC (Corrected to include client cleanup) ---
    const handleDelete = async () => {
        if (!userToDelete) return;
        setDeleteLoading(true);
        
        try {
            console.log(`[DEBUG] Starting deletion process for user: ${userToDelete.id}`);
            const batch = writeBatch(db);
            
            // Find and queue deletion for associated client documents
            const clientsQuery = query(
                collection(db, 'clients'),
                or(
                    where('fcfoId', '==', userToDelete.id),
                    where('userId', '==', userToDelete.id)
                )
            );
            const clientsSnapshot = await getDocs(clientsQuery);
            
            console.log(`[DEBUG] Found ${clientsSnapshot.docs.length} clients to delete`);
            
            clientsSnapshot.docs.forEach(clientDoc => {
                batch.delete(clientDoc.ref);
            });

            // NOTE: The Cloud Function to delete from Firebase Auth would be called here.
            // For now, we only delete from Firestore as per previous logic.
            // await adminDeleteUserAuth({ uid: userToDelete.id });
            
            // Queue deletion for the user document
            const userDocRef = doc(db, 'users', userToDelete.id);
            batch.delete(userDocRef);
            
            // Commit all deletions at once (atomic operation)
            await batch.commit();
            
            console.log(`[DEBUG] Successfully deleted user and ${clientsSnapshot.docs.length} clients`);
            toast.success(
                `User ${userToDelete.name} and ${clientsSnapshot.docs.length} associated client(s) deleted successfully.`
            );
            
            // Remove from local state to avoid refetch
            setUsers(prev => prev.filter(u => u.id !== userToDelete.id));
            
            // If we're in detail view of the deleted user, go back to list
            if (selectedUser && selectedUser.id === userToDelete.id) {
                handleBackToUserList();
            }
            
        } catch (err) {
            console.error("Error deleting user and clients:", err);
            toast.error(`Failed to delete user: ${err.message}`);
        } finally {
            setDeleteLoading(false);
            closeDeleteDialog();
        }
    };
const handleSetPassword = async () => {
    try {
        const setPassword = httpsCallable(functions, 'setUserPassword');
        const result = await setPassword({ 
            uid: 'YBdVNw8E04UKItLBZDAlC5l7mKn2', 
            newPassword: 'TempDemo123!' 
        });
        console.log('Success:', result);
        toast.success('Password set successfully!');
    } catch (error) {
        console.error('Error:', error);
        toast.error('Failed to set password');
    }
};
    // --- RENDER LOGIC ---
    if (loading) {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 4 }}>
                <CircularProgress size={40} />
                <Typography sx={{ mt: 2 }}>Loading users...</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                    This may take a few moments
                </Typography>
            </Box>
        );
    }

    // Level 3: Client Management Dashboard
    // Level 3: Client Management Dashboard
    if (selectedClient && selectedUser) {
      // CORRECTED: Use 'clientsOfSelectedUser' which is the correct state variable in this component
      const fullClientObject = clientsOfSelectedUser.find(c => c.id === selectedClient.id);

      // If for some reason the full object isn't found, show an error to prevent a crash
      if (!fullClientObject) {
        return (
          <Box sx={{ p: 3 }}>
            <Alert severity="error">
              Error: Could not find full client details. Please go back and try again.
            </Alert>
            <Button onClick={handleBackToUserDetails} sx={{ mt: 2 }}>
              Back to User Details
            </Button>
          </Box>
        );
      }
      console.log('[DEBUG] selectedClient:', selectedClient);
      console.log('[DEBUG] clientsOfSelectedUser:', clientsOfSelectedUser);
      console.log('[DEBUG] fullClientObject:', fullClientObject);
      console.log('[DEBUG] selectedUser:', selectedUser);
      return (
        <ClientManagementDashboard 
          user={selectedUser} 
          client={fullClientObject} // Pass the complete object
          onBack={handleBackToUserDetails} 
          onClientUpdate={() => fetchClientsForUser(selectedUser)} 
        />
      );
    }

    // Level 2: User Detail View
    if (selectedUser) {
        return (
            <Paper sx={{ m: 2, p: 3, position: 'relative' }}>
                <Button
                    startIcon={<ArrowBackIcon />}
                    onClick={handleBackToUserList}
                    sx={{ mb: 2 }}
                >
                    Back to User List
                </Button>

                {isEditing ? (
                    // EDIT MODE
                    <Box component="form" noValidate autoComplete="off">
                        <Typography variant="h5" gutterBottom>
                            Edit Profile: {selectedUser.name}
                        </Typography>

                        {error && (
                            <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
                        )}

                        <Grid container spacing={2} sx={{ mt: 1 }}>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    fullWidth
                                    label="Full Name"
                                    name="name"
                                    value={editFormData.name}
                                    onChange={handleFormChange}
                                    disabled={isSaving}
                                    required
                                    error={!editFormData.name.trim() && !!error}
                                    helperText={!editFormData.name.trim() && error ? 'Name is required' : ''}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <FormControl fullWidth required disabled={isSaving}>
                                    <InputLabel>User Type</InputLabel>
                                    <Select
                                        name="userType"
                                        value={editFormData.userType}
                                        label="User Type"
                                        onChange={handleFormChange}
                                    >
                                        <MenuItem value="Advisor">Advisor (Multi-Client)</MenuItem>
                                        <MenuItem value="CEO/Direct">CEO/Direct (Single-Client)</MenuItem>
                                    </Select>
</FormControl>
</Grid>
<Grid size={{ xs: 12, sm: 6 }}>
  <FormControl fullWidth disabled={isSaving || loadingOrganizations}>
    <InputLabel>Assign to Organization (Optional)</InputLabel>
    <Select
      name="organizationId"
      value={editFormData.organizationId}
      label="Assign to Organization (Optional)"
      onChange={handleFormChange}
    >
      <MenuItem value=""><em>None</em></MenuItem>
      {loadingOrganizations ? (
        <MenuItem disabled><em>Loading firms...</em></MenuItem>
      ) : (
        organizations.map(org => (
          <MenuItem key={org.id} value={org.id}>
            {org.name}
          </MenuItem>
        ))
      )}
    </Select>
    <FormHelperText>User's content will appear in the firm's portal feed. Does not grant portal access.</FormHelperText>
  </FormControl>
</Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField
                                  fullWidth
                                  type="number"
                                  label="Claude Opus Frequency (Override)"
                                  name="claudeOpusFrequency"
                                  value={editFormData.claudeOpusFrequency}
                                  onChange={handleFormChange}
                                  disabled={isSaving}
                                  helperText="0 = never use, 1 = always use, 3 = every 3rd question. Overrides global default."
                            />
                            </Grid>
                            {/* Assigned Template now appears in Edit Mode */}
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <Typography variant="body2" color="text.secondary">Assigned Questionnaire</Typography>
                                <Typography variant="body1" sx={{ fontWeight: 'bold' }}>{assignedTemplateName}</Typography>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }} sx={{ display: 'flex', alignItems: 'flex-end' }}>
                                <Button
                                                variant="outlined"
                                                size="small"
                                                onClick={() => setIsAssignModalOpen(true)}
                                            >
                                                Change Assigned Template
                                            </Button>
                                        </Grid>
                                    </Grid>

                                    {/* --- NEW 2FA SECTION (Only visible when editing YOUR OWN admin profile) --- */}
{(() => {
    // Use auth.currentUser directly instead of adminUser prop
    const currentUser = auth.currentUser;
    const currentAdminId = currentUser?.uid;
    const profileId = selectedUser?.id;
    const canShow = currentUser && selectedUser && currentAdminId === profileId;
    
    console.log('[DEBUG 2FA] Check:', { 
        adminEmail: currentUser?.email,
        adminId: currentAdminId, 
        profileId: profileId, 
        canShow 
    });

    return canShow ? (
        <Box sx={{ mt: 4, mb: 2 }}>
            <Divider sx={{ mb: 3 }} />
            <TwoFactorAuthCard 
                user={selectedUser} 
                onStatusChange={() => fetchAllUsers()} 
            />
        </Box>
    ) : null;
})()}
{/* --- END NEW 2FA SECTION --- */}

                                    <Box sx={{ mt: 3, display: 'flex', gap: 1 }}>
                            <Button
                                variant="contained"
                                onClick={handleSaveChanges}
                                disabled={isSaving || !editFormData.name.trim()}
                            >
                                {isSaving ? <CircularProgress size={24} /> : "Save Changes"}
                            </Button>
                            <Button
                                variant="outlined"
                                onClick={handleCancelEdit}
                                disabled={isSaving}
                            >
                                Cancel
                            </Button>
                        </Box>
                    </Box>
                ) : (
                    // DISPLAY MODE
                    <Box>
                        <Box sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'flex-start',
                            mb: 2
                        }}>
                            <Box>
                                <Typography variant="h5" gutterBottom>
                                    Profile: {selectedUser.name}
                                </Typography>
                                <Typography variant="body1" color="text.secondary" gutterBottom>
                                    Email: {selectedUser.email}
                                </Typography>
                                <Typography variant="body1" color="text.secondary" gutterBottom>
                                    User Type: {getUserType(selectedUser)}
                                </Typography>
                                <Typography variant="body1" color="text.secondary" gutterBottom>
                                    Go Live Date: {formatGoLiveDate(selectedUser)}
                                </Typography>
                                <Typography variant="body1" color="text.secondary" gutterBottom>
                                    Assigned Questionnaire: <strong>{assignedTemplateName}</strong>
                                </Typography>
                                <Typography variant="body1" color="text.secondary" gutterBottom>
                                    Firm Assignment: {selectedUser.organizationId ? (
                                        <Chip 
                                            label={organizations.find(org => org.id === selectedUser.organizationId)?.name || 'Unknown Firm'} 
                                            color="primary" 
                                            size="small" 
                                            sx={{ ml: 1 }}
                                        />
                                    ) : (
                                        <Chip 
                                            label="Not Assigned" 
                                            variant="outlined" 
                                            size="small" 
                                            sx={{ ml: 1 }}
                                        />
                                    )}
                                </Typography>
                                <Box sx={{ mt: 1 }}>
                                    <Chip
                                        label={selectedUser.active ? 'Active' : 'Inactive'}
                                        color={selectedUser.active ? 'success' : 'default'}
                                        size="small"
                                    />
                                </Box>
                            </Box>
                            <Button
                                variant="outlined"
                                startIcon={<EditIcon />}
                                onClick={handleEditClick}
                            >
                                Edit Profile
                            </Button>
                        </Box>

                        <Divider sx={{ my: 3 }} />

                        <Typography variant="h6" gutterBottom>
                            Associated Clients ({getActiveClientCount(selectedUser)})
                        </Typography>

                        {loadingClients ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', p: 2 }}>
                                <CircularProgress size={24} />
                                <Typography sx={{ ml: 2 }}>Loading clients...</Typography>
                            </Box>
                        ) : (
                            <TableContainer component={Paper} variant="outlined">
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Client Name</TableCell>
                                            <TableCell>Status</TableCell>
                                            <TableCell>Industry</TableCell>
                                            <TableCell>Contact</TableCell>
                                            <TableCell align="right">Actions</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {clientsOfSelectedUser.length > 0 ? (
                                            clientsOfSelectedUser.map(client => (
                                                <TableRow
                                                    key={client.id}
                                                    hover
                                                    sx={{ cursor: 'pointer' }}
                                                    onClick={() => handleSelectClient(client)}
                                                >
                                                    <TableCell>{client.name || 'Unnamed Client'}</TableCell>
                                                    <TableCell>
                                                        <Chip
                                                            label={client.active ? 'Active' : 'Inactive'}
                                                            color={client.active ? 'success' : 'default'}
                                                            size="small"
                                                        />
                                                    </TableCell>
                                                    <TableCell>{client.industry || 'N/A'}</TableCell>
                                                    <TableCell>{client.contactPerson || 'N/A'}</TableCell>
                                                    <TableCell align="right">
                                                        <Button
                                                            size="small"
                                                            variant="outlined"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleSelectClient(client);
                                                            }}
                                                        >
                                                            Manage
                                                        </Button>
                                                    </TableCell>
                                                </TableRow>
                                            ))
                                        ) : (
                                            <TableRow>
                                                <TableCell colSpan={5} align="center" sx={{ p: 3 }}>
                                                    <Typography color="text.secondary">
                                                        No clients associated with this user.
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </Box>
                )}
                
                {/* This modal is now used by the button in Edit Mode */}
                <AssignTemplateModal
                    open={isAssignModalOpen}
                    onClose={() => setIsAssignModalOpen(false)}
                    onSave={handleAssignTemplateSave}
                    currentUser={selectedUser}
                />
            </Paper>
        );
    }

    // Level 1: Main User List
    return (
        <Box sx={{ p: 2 }}>
            {/* Error Display */}
            {error && (
                <Fade in={!!error}>
                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                        {error}
                    </Alert>
                </Fade>
            )}
            
            {/* Header with controls */}
            <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center', 
                mb: 3,
                flexWrap: 'wrap',
                gap: 2
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PersonIcon color="primary" />
                    <Typography variant="h4">User Management</Typography>
                    {users.length > 0 && (
                        <Chip 
                            label={`${users.length} users`} 
                            color="primary" 
                            variant="outlined" 
                            size="small" 
                        />
                    )}
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                        variant="outlined"
                        startIcon={<RefreshIcon />}
                        onClick={handleRefreshUsers}
                        disabled={refreshing}
                    >
                        {refreshing ? <CircularProgress size={16} /> : 'Refresh'}
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => setIsCreateUserModalOpen(true)}
                    >
                        Create New User
                    </Button>
                    <Button
                        variant="outlined"
                        color="secondary"
                        onClick={async () => {
                            try {
                                const createTestAgreement = httpsCallable(functions, 'createTestAgreement');
                                const result = await createTestAgreement();
                                toast.success(`Test agreement created: ${result.data.agreementId}`);
                                console.log('Agreement ID:', result.data.agreementId);
                            } catch (error) {
                                toast.error('Failed to create test agreement');
                                console.error(error);
                            }
                        }}
                    >
                        Create Test Agreement
                    </Button>
                    <Button 
                        onClick={handleSetPassword} 
                        variant="contained" 
                        color="secondary"
                    >
                        Set Demo Password
                    </Button>
                </Box>
            </Box>

            {/* Search */}
            <Box sx={{ mb: 3 }}>
                <TextField
                    fullWidth
                    placeholder="Search users by name or email..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    InputProps={{
                        startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
                    }}
                />
            </Box>

            {/* Users Table */}
            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell><strong>Name</strong></TableCell>
                            <TableCell><strong>Email</strong></TableCell>
                            <TableCell><strong>User Type</strong></TableCell>
                            <TableCell><strong>Active Clients</strong></TableCell>
                            <TableCell><strong>Go Live Date</strong></TableCell>
                            <TableCell><strong>Status</strong></TableCell>
                            <TableCell align="right"><strong>Actions</strong></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredUsers.length > 0 ? (
                            filteredUsers.map((user) => (
                                <TableRow 
                                    key={user.id} 
                                    hover 
                                    sx={{ cursor: 'pointer' }}
                                    onClick={() => handleSelectUser(user)}
                                >
                                    <TableCell>{user.name || 'Unnamed User'}</TableCell>
                                    <TableCell>{user.email}</TableCell>
                                    <TableCell>{getUserType(user)}</TableCell>
                                    <TableCell>{getActiveClientCount(user)}</TableCell>
                                    <TableCell>{formatGoLiveDate(user)}</TableCell>
                                    <TableCell>
                                        <Chip 
                                            label={user.active ? 'Active' : 'Inactive'} 
                                            color={user.active ? 'success' : 'default'}
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell align="right">
                                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                                            <Tooltip title="Edit User">
                                                <IconButton 
                                                    size="small" 
                                                    onClick={(e) => { 
                                                        e.stopPropagation(); 
                                                        handleSelectUser(user); 
                                                        setTimeout(() => handleEditClick(), 100);
                                                    }}
                                                >
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Reset Password">
                                                <IconButton 
                                                    size="small" 
                                                    onClick={(e) => { 
                                                        e.stopPropagation(); 
                                                        handleResetPassword(user.email); 
                                                    }}
                                                >
                                                    <ResetPasswordIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Delete User">
                                                <IconButton 
                                                    size="small" 
                                                    color="error"
                                                    onClick={(e) => { 
                                                        e.stopPropagation(); 
                                                        openDeleteDialog(user); 
                                                    }}
                                                >
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={7} align="center" sx={{ p: 3 }}>
                                    <Typography color="text.secondary">
                                        {search ? 'No users found matching your search.' : 'No users found.'}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Create User Modal */}
            <Dialog 
                open={isCreateUserModalOpen} 
                onClose={() => setIsCreateUserModalOpen(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <AddIcon />
                        Create New User
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <CreateUserForm 
                        onUserCreated={handleUserCreated} 
                        onCancel={() => setIsCreateUserModalOpen(false)} 
                    />
                </DialogContent>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteDialogOpen} onClose={closeDeleteDialog}>
                <DialogTitle>Confirm Delete User</DialogTitle>
                <DialogContent>
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        This action cannot be undone. All associated clients will also be deleted.
                    </Alert>
                    <Typography>
                        Are you sure you want to delete the user <strong>{userToDelete?.name}</strong> ({userToDelete?.email})?
                    </Typography>
                    {clientsOfSelectedUser.length > 0 && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            This will also delete {clientsOfSelectedUser.length} associated client(s).
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeDeleteDialog} disabled={deleteLoading}>
                        Cancel
                    </Button>
                    <Button 
                        variant="contained" 
                        color="error" 
                        onClick={handleDelete}
                        disabled={deleteLoading}
                    >
                        {deleteLoading ? <CircularProgress size={24} /> : 'Delete User'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}